<script setup>
// S16 R12 — Connect Shopify (REAL OAuth redirect).
// Step 1: operator enters a store domain -> canonicalized to *.myshopify.com.
// Step 2: confirm -> dashboard requests a signed install URL from ai-back.
// Step 3: the browser is REALLY redirected to Shopify's install_url. Shopify then
//         returns server-to-server to the ai-back callback (the browser does NOT drive
//         the callback). On return the operator lands back in the workspace via the
//         signed state's return_url. No token ever appears here; only { install_url }.
import { ref, computed } from "vue";
import { useCompanyStore } from "@/stores/company";
import { useUiStore } from "@/stores/ui";
import { useShopifyStore } from "@/stores/shopify";
import { canonicalShopDomain, isValidShopDomain } from "@/utils/helpers/shopify";

const emit = defineEmits(["connected", "close"]);
const uiStore = useUiStore();
const shopifyStore = useShopifyStore();
const company_id = useCompanyStore().companyId;

// The dashboard return target Shopify bounces back to after server-side callback. The
// workspace re-opens for this integration via the ?shopify= URL param (R12 URL state).
function buildReturnUrl() {
  if (typeof window === "undefined" || !window.location) return undefined;
  return `${window.location.origin}${window.location.pathname}`;
}

// Perform the REAL browser navigation to Shopify's consent screen. Injectable so a jsdom
// test can assert the redirect target without an actual navigation.
function redirectTo(url) {
  if (typeof window !== "undefined" && window.location) {
    window.location.assign(url);
  }
}

const getModalAction = computed(() => uiStore.modalAction);

const step = ref("enter"); // enter | confirm | authorizing | redirecting
const rawDomain = ref("");
const installUrl = ref(null);
const oauthState = ref(null);
const errorMessage = ref("");

const canonical = computed(() => canonicalShopDomain(rawDomain.value));
const domainValid = computed(() => isValidShopDomain(rawDomain.value));

const resetAll = () => {
  step.value = "enter";
  rawDomain.value = "";
  installUrl.value = null;
  oauthState.value = null;
  errorMessage.value = "";
};

const close = () => {
  resetAll();
  uiStore.resetModalStore();
  emit("close");
};

const toConfirm = () => {
  errorMessage.value = "";
  if (!domainValid.value) {
    errorMessage.value = "Enter a valid myshopify.com store domain.";
    return;
  }
  step.value = "confirm";
};

const requestInstall = async () => {
  errorMessage.value = "";
  step.value = "authorizing";
  try {
    const res = await shopifyStore.requestInstallUrl(
      company_id,
      rawDomain.value,
      buildReturnUrl()
    );
    installUrl.value = res?.install_url || res?.data?.install_url || null;
    oauthState.value = res?.state || res?.data?.state || null;
    if (!installUrl.value) {
      throw { data: { message: "No install URL was returned." } };
    }
    // REAL redirect: hand the browser to Shopify's consent screen. Shopify returns
    // server-to-server to the ai-back callback; the browser is NOT the callback writer.
    step.value = "redirecting";
    redirectTo(installUrl.value);
  } catch (e) {
    errorMessage.value = e?.data?.message || "Could not start the connection.";
    step.value = "confirm";
  }
};

defineExpose({ step, requestInstall, toConfirm, close, redirectTo, buildReturnUrl });
</script>

<template>
  <DModal
    v-if="getModalAction === 'connect_shopify'"
    :visible="uiStore.modalState"
    @update:visible="(val) => { if (!val) close(); }"
    :main_classes="[]"
    :dialog_classes="[]"
  >
    <template #title> Connect Shopify </template>

    <template #body>
      <div v-if="errorMessage" class="shopify-alert" role="alert" data-test="connect-error">
        {{ errorMessage }}
      </div>

      <!-- Step: enter domain -->
      <div v-if="step === 'enter'">
        <p class="text-gray-500 mb-2">
          Enter your Shopify store domain. We only request catalogue, location and
          inventory read access — never orders, customers or payments.
        </p>
        <DInput
          :value="rawDomain"
          @input="rawDomain = $event.target.value"
          label="Store domain (e.g. acme.myshopify.com)"
          width="100%"
        />
        <p v-if="rawDomain && canonical" class="hint-input mt-1" data-test="canonical-preview">
          Will connect: <b>{{ canonical }}</b>
        </p>
        <p v-else-if="rawDomain && !domainValid" class="shopify-hint-error mt-1">
          That is not a valid myshopify.com domain.
        </p>
      </div>

      <!-- Step: confirm -->
      <div v-else-if="step === 'confirm'">
        <p class="mb-2">You are about to connect:</p>
        <p class="shopify-confirm-domain"><b>{{ canonical }}</b></p>
        <p class="text-gray-500 mt-2">
          You will be taken to Shopify to approve read-only access. Credentials are
          stored securely by Ravo and are never shown here.
        </p>
      </div>

      <!-- Step: authorizing -->
      <div v-else-if="step === 'authorizing'" class="shopify-center" data-test="connect-authorizing">
        <span class="spinner-sm" role="status" aria-hidden="true"></span>
        <p class="mt-2">Authorizing with Shopify…</p>
      </div>

      <!-- Step: redirecting to Shopify (real OAuth) -->
      <div v-else-if="step === 'redirecting'" class="shopify-center" data-test="connect-redirecting">
        <span class="spinner-sm" role="status" aria-hidden="true"></span>
        <p class="mt-2">Redirecting you to Shopify to approve read-only access…</p>
      </div>
    </template>

    <template #footer>
      <div class="w-full">
        <div class="seperator text-center"></div>
        <div class="flex mt-20 justify-center">
          <button
            v-if="step === 'enter'"
            :disabled="!domainValid"
            @click="toConfirm"
            type="button"
            class="btn btn-primary add-button mr-30"
            data-test="btn-next"
          >
            Next
          </button>

          <button
            v-if="step === 'confirm'"
            @click="requestInstall"
            type="button"
            class="btn btn-primary add-button mr-30"
            data-test="btn-connect"
          >
            Connect to Shopify
          </button>

          <button
            v-if="step !== 'authorizing' && step !== 'redirecting'"
            @click="close"
            type="button"
            class="btn btn-secondary cancel-button"
            data-test="btn-cancel"
          >
            Cancel
          </button>
        </div>
      </div>
    </template>
  </DModal>
</template>

<style lang="scss" scoped>
.shopify-alert {
  background: var(--red-50, #fef2f2);
  color: var(--red-700, #b91c1c);
  border: 1px solid var(--red-200, #fecaca);
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 12px;
  font-size: var(--text-sm);
}
.shopify-hint-error { color: var(--red-600, #dc2626); font-size: var(--text-xs); }
.shopify-confirm-domain { font-size: 18px; }
.shopify-center { text-align: center; padding: 16px 0; }
.shopify-ok { font-size: 32px; color: var(--green-500, #22c55e); }
</style>
